import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Zap, Star, Shield, Loader2, X } from 'lucide-react';
import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import SubscriptionCheckoutModal from '../../componentes/SubscriptionModal';

const PLAN_NAME_MAP = {
    plus:    'Plano Plus - Mensal',
    premium: 'Plano Premium - Mensal',
};

const PLAN_AMOUNT_MAP = {
    plus:    12.90,
    premium: 21.90,
};

const PLAN_ORDER = { free: 0, plus: 1, premium: 2 };

const PLANS = [
    {
        id: 'free', name: 'Gratuito', price: 'R$ 0', period: '/mês',
        desc: 'Ideal para testar e começar.', icon: <Shield size={22} />, color: 'gray',
        benefits: ['1 nota por semana', '5 produtos no estoque', 'Suporte básico por e-mail', 'PDF das notas'],
    },
    {
        id: 'plus', name: 'Plus', price: 'R$ 12,90', period: '/mês',
        desc: 'Para freelancers em crescimento.', icon: <Zap size={22} />, color: 'indigo', highlight: true,
        benefits: ['5 notas por semana', '25 produtos no estoque', 'Suporte prioritário', 'Relatórios mensais', 'Backup automático'],
    },
    {
        id: 'premium', name: 'Premium', price: 'R$ 21,90', period: '/mês',
        desc: 'Para quem não pode ter limites.', icon: <Star size={22} />, color: 'gold',
        benefits: ['Notas ilimitadas', '250 produtos no estoque', 'Suporte 24/7', 'Backup automático', 'Relatórios avançados', 'Acesso antecipado a novidades'],
    },
];

const FAQ_ITEMS = [
    { q: 'Posso cancelar a qualquer momento?',     a: 'Sim. Cancele pelo painel sem fidelidade. O acesso continua até o fim do período pago.' },
    { q: 'O pagamento é seguro?',                  a: 'Sim. Usamos o Mercado Pago com criptografia de ponta a ponta e cobrança automática mensal.' },
    { q: 'O plano gratuito expira?',               a: 'Não. O plano gratuito é permanente, com limite de 1 nota por semana.' },
    { q: 'Como funciona a assinatura recorrente?', a: 'Após assinar, o Mercado Pago cobra automaticamente todo mês. Você pode cancelar a qualquer momento.' },
];

export default function Planos() {
    const { authenticated, user, cancelSubscription, fetchUser } = useContext(Context);
    const navigate = useNavigate();

    const [loadingPlan, setLoadingPlan]           = useState(null);
    const [confirmDowngrade, setConfirmDowngrade] = useState(null);
    const [checkoutPlan, setCheckoutPlan]         = useState(null); // plano em processo de checkout via Brick

    const planoAtual  = user?.plan || 'free';
    const token       = localStorage.getItem('token') ? JSON.parse(localStorage.getItem('token')) : null;

    /* ── executa a troca de plano ── */
    async function executarTroca(plan) {
        setConfirmDowngrade(null);

        // Deslogado → manda para cadastro
        if (!authenticated) {
            navigate(`/register?plan=${plan.id}`);
            return;
        }

        // Downgrade para free: cancela a assinatura ativa
        if (plan.id === 'free') {
            setLoadingPlan(plan.id);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API}/subscriptions/my`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                const activeSub = (data.subscriptions || []).find(s => s.status !== 'cancelled');
                if (activeSub) {
                    await fetch(
                        `${process.env.REACT_APP_API}/subscriptions/${activeSub._id}/cancel`,
                        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
                    );
                }
            } catch (err) {
                console.error('Erro ao cancelar assinatura:', err);
            }
            await fetchUser();
            setLoadingPlan(null);
            return;
        }

        // Upgrade / troca entre planos pagos → abre o checkout (Card Brick)
        setCheckoutPlan({
            planName: PLAN_NAME_MAP[plan.id],
            name:     plan.name,
            amount:   PLAN_AMOUNT_MAP[plan.id],
        });
    }

    function handleAssinar(plan) {
        if (!authenticated) { navigate(`/register?plan=${plan.id}`); return; }
        if (plan.id === planoAtual) return;

        const currentOrder = PLAN_ORDER[planoAtual] ?? 0;
        const targetOrder  = PLAN_ORDER[plan.id]    ?? 0;

        if (targetOrder < currentOrder) {
            setConfirmDowngrade(plan);
            return;
        }

        executarTroca(plan);
    }

    function getButtonLabel(plan) {
        if (!authenticated) return plan.id === 'free' ? 'Começar grátis' : `Assinar ${plan.name}`;
        if (plan.id === planoAtual) return '✓ Plano atual';
        const currentOrder = PLAN_ORDER[planoAtual] ?? 0;
        const targetOrder  = PLAN_ORDER[plan.id]    ?? 0;
        if (targetOrder < currentOrder) return 'Fazer downgrade';
        return plan.id === 'free' ? 'Usar gratuitamente' : `Assinar ${plan.name}`;
    }

    function isCurrentPlan(plan)  { return authenticated && plan.id === planoAtual; }
    function isDisabled(plan)     { return loadingPlan !== null || isCurrentPlan(plan); }

    return (
        <div className={styles.page}>
            <Helmet>
                <title>Planos e Preços — Orçamento Digital</title>
                <meta
                    name="description"
                    content="Compare os planos Gratuito, Plus e Premium do Orçamento Digital. Escolha o plano ideal para emitir orçamentos e notas de serviço."
                />
                <link rel="canonical" href="https://www.orcamento-digital.com/planos" />
            </Helmet>
            <div className={styles.header}>
                <h1 className={styles.title}>Planos de Assinatura</h1>
                <p className={styles.subtitle}>
                    Cobrança automática mensal via Mercado Pago. Cancele quando quiser.
                </p>
                {authenticated && planoAtual !== 'free' && (
                    <div className={styles.currentPlanBanner}>
                        Você está no plano <strong>{planoAtual.charAt(0).toUpperCase() + planoAtual.slice(1)}</strong>.
                        {' '}
                        <button className={styles.cancelLink}
                            onClick={() => setConfirmDowngrade(PLANS.find(p => p.id === 'free'))}>
                            Cancelar assinatura
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.cards}>
                {PLANS.map(plan => (
                    <div key={plan.id}
                        className={`${styles.card} ${plan.highlight ? styles.highlight : ''} ${styles[`card_${plan.color}`]} ${isCurrentPlan(plan) ? styles.cardCurrent : ''}`}>

                        {plan.highlight && !isCurrentPlan(plan) && (
                            <span className={styles.badge}>⚡ Mais popular</span>
                        )}
                        {isCurrentPlan(plan) && (
                            <span className={`${styles.badge} ${styles.badgeCurrent}`}>✓ Seu plano</span>
                        )}

                        <div className={`${styles.planIcon} ${styles[`icon_${plan.color}`]}`}>
                            {plan.icon}
                        </div>
                        <h2 className={styles.planName}>{plan.name}</h2>
                        <p className={styles.planDesc}>{plan.desc}</p>

                        <div className={styles.priceRow}>
                            <span className={styles.price}>{plan.price}</span>
                            <span className={styles.period}>{plan.period}</span>
                        </div>

                        <ul className={styles.benefits}>
                            {plan.benefits.map((b, i) => (
                                <li key={i}>
                                    <CheckCircle size={15} className={styles.check} /> {b}
                                </li>
                            ))}
                        </ul>

                        <button
                            className={`${styles.btn} ${plan.highlight && !isCurrentPlan(plan) ? styles.btnHighlight : styles.btnDefault} ${isCurrentPlan(plan) ? styles.btnCurrent : ''}`}
                            onClick={() => handleAssinar(plan)}
                            disabled={isDisabled(plan)}
                        >
                            {loadingPlan === plan.id
                                ? <Loader2 size={16} className={styles.spinner} />
                                : getButtonLabel(plan)
                            }
                        </button>

                        {!authenticated && plan.id !== 'free' && (
                            <p className={styles.loginHint}>
                                Já tem conta? <Link to={`/login?plan=${plan.id}`}>Entrar e assinar</Link>
                            </p>
                        )}
                    </div>
                ))}
            </div>

            <div className={styles.mpRow}>
                <img
                    src="https://imgmp.mlstatic.com/org-img/banners/br/medios/online/468X60.jpg"
                    alt="Mercado Pago - meios de pagamento"
                    className={styles.mpBadge}
                />
            </div>

            <div className={styles.faqSection}>
                <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
                <div className={styles.faqGrid}>
                    {FAQ_ITEMS.map((item, i) => (
                        <div key={i} className={styles.faqCard}>
                            <h3 className={styles.faqQ}>{item.q}</h3>
                            <p className={styles.faqA}>{item.a}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Modal confirmação de downgrade ── */}
            {confirmDowngrade && (
                <div className={styles.overlayConfirm}>
                    <div className={styles.modalConfirm}>
                        <button className={styles.modalClose}
                            onClick={() => setConfirmDowngrade(null)}>
                            <X size={18} />
                        </button>

                        <h3 className={styles.modalTitle}>
                            {confirmDowngrade.id === 'free'
                                ? 'Cancelar assinatura?'
                                : `Fazer downgrade para ${confirmDowngrade.name}?`
                            }
                        </h3>
                        <p className={styles.modalText}>
                            {confirmDowngrade.id === 'free'
                                ? 'Sua assinatura recorrente será cancelada no Mercado Pago e seu plano voltará para Gratuito imediatamente.'
                                : `Sua assinatura atual será cancelada e um novo checkout será aberto para o plano ${confirmDowngrade.name}.`
                            }
                        </p>

                        <div className={styles.modalActions}>
                            <button className={styles.modalConfirmBtn}
                                onClick={() => executarTroca(confirmDowngrade)}
                                disabled={loadingPlan !== null}>
                                {loadingPlan === confirmDowngrade.id
                                    ? <Loader2 size={15} className={styles.spinner} />
                                    : 'Confirmar'
                                }
                            </button>
                            <button className={styles.modalCancelBtn}
                                onClick={() => setConfirmDowngrade(null)}>
                                Voltar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal do Card Payment Brick ── */}
            {checkoutPlan && (
                <SubscriptionCheckoutModal
                    plan={checkoutPlan}
                    payerEmail={user?.email}
                    onClose={() => setCheckoutPlan(null)}
                    onSuccess={async () => {
                        setCheckoutPlan(null);
                        await fetchUser();
                    }}
                />
            )}
        </div>
    );
}
